import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { PageContent } from '../../../../components/page-components/PageContent';
import { Switch, Route, Redirect } from 'react-router';
import { NavRibbon, NavRibbonLink } from '../../../../components/nav-ribbon/NavRibbon';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { ApiEndpoints, PageNames, Routes } from '../../../../utils/app-constants';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { StatusCode } from '../../../../utils/api';
import { PhysicalInventoryDetails } from '../../../../modules/inventory/models/physical-inventory-details';
import { navigateToPage } from '../../../../utils/history';
import { InventoryPhysicalControlGroupsScene } from './InventoryPhysicalControlGroups';
import { InventoryPhysicalHorizontalEntryEditScene } from './InventoryPhysicalHorizontalEntryEdit';
import { InventoryPhysicalVerticalEntryEditScene } from './InventoryPhysicalVerticalEntryEdit';
import { InventoryPhysicalResolveConflictsScene } from './InventoryPhysicalResolveConflicts';
import { InventoryPhysicalShipmentsScene } from './InventoryPhysicalShipments';
import { InventoryPhysicalOrdersScene } from './InventoryPhysicalOrdersScene';
import { InventoryPhysicalAdjustmentsScene } from './InventoryPhysicalAdjustments';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { InventoryPhysicalReportScene } from './InventoryPhysicalReport';
import { PhysicalInventoryPricingStatus, PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';
import { ChiTooltip } from '../../../../components/utility/ChiTooltip';

export enum PhysicalInventoryHeaderScenario {
    ControlGroups,
    Shipments,
    Orders,
    Adjustments,
    Report
}

export const InventoryPhysicalDetailsScene = (props: IBaseSceneProps) => {

    const [service] = React.useState(new InventorySvc());
    const [inventoryDetails, setInventoryDetails] = React.useState(new PhysicalInventoryDetails());
    
    const editEntryRef: any = React.useRef();
    const shipmentsRef: any = React.useRef();
    const ordersRef: any = React.useRef();
    const adjustmentsRef: any = React.useRef();
    const reportRef: any = React.useRef();

    async function getDetails(id: string) {

        if (id) {
            const result = await service.getPhysicalReportDetails(id);
            if (result.status === StatusCode.Success) {
                setInventoryDetails(result.data);
                return;
            }
        }
        else {
            navigateToPage(ApiEndpoints.INVENTORY_PHYSICAL_REPORTS);
        }
    }

    function getId() {
        const opts = props.location.pathname.split('/');
        return opts[4];
    }

    function onRefreshPricing() {
        var id = getId();
        service.refreshPricing(id)
            .then((result) => {
                var tmpInventoryDetails = { ...inventoryDetails };
                tmpInventoryDetails.pricingStatus = PhysicalInventoryPricingStatus.refreshing;
                setInventoryDetails(tmpInventoryDetails);
            });
    }

    const getButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalRefreshPricing
        ]}>
            {
                inventoryDetails.pricingStatus == PhysicalInventoryPricingStatus.error
                    ? <ChiTooltip
                        title="Error"
                        body={<p>Refresh pricing ended with an error.</p>}
                        content={ <i className="la la-warning pricing-error"></i> }
                    />
                    : null
            }
            {
                inventoryDetails.pricingStatus == PhysicalInventoryPricingStatus.processed
                    ? <ChiTooltip
                        title="Success"
                        body={<p>Refresh pricing has run successfully at least once.</p>}
                        content={<i className="la la-check pricing-complete"></i>}
                    />
                    : null
            }
            {
                inventoryDetails.pricingStatus == PhysicalInventoryPricingStatus.refreshing
                    ? <ChiTooltip
                        title="In Progress"
                        body={<p>Refresh pricing is in progress.</p>}
                        content={<i className="la la-refresh pricing-not-started"></i>}
                    />
                    : null
            }    
            {
                inventoryDetails.pricingStatus == PhysicalInventoryPricingStatus.notStarted
                    ? <ChiTooltip
                        title="Not Started"
                        body={<p>Refresh pricing has not been run.</p>}
                        content={<i className="la la-play  pricing-not-started"></i>}
                    />
                    : null
            }    
            <button className="btn attention" onClick={onRefreshPricing} disabled={inventoryDetails.status == PhysicalInventoryStatus.complete || inventoryDetails.pricingStatus == PhysicalInventoryPricingStatus.refreshing }>
                REFRESH PRICING
            </button>
        </HasPermission>;
    }

    React.useEffect(() => {
        getDetails(getId());
    }, []);

    const goBack = () => {
        const opts = props.location.pathname.split('/');
        var scenario = opts[5];

        switch (scenario) {
            case "controlgroups":
                if (opts.length > 6 && opts[6] == "entries") {
                    var controlNumber = opts[8];
                    var reportId = opts[4];
                    navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));                    
                }
                else {
                    navigateToPage(Routes.INVENTORY_PHYSICAL);
                }
                break;
            case "conflicts":
                var reportId = opts[4];
                navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
            case "shipments":
                shipmentsRef?.current?.goBack();
            case "orders":
                ordersRef?.current?.goBack();
            case "adjustments":
                adjustmentsRef?.current?.goBack();
            case "report":
                reportRef?.current?.goBack();
            default:
                //do nothing
                break;
        }
    };

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_DETAILS} classes='inventory-physical'>
        <PageHeading showBackBtn={true} onBackClick={goBack} title={inventoryDetails.name}>
            <div className="row">
                <div className="col">
                    {getButtons()}
                </div>
            </div>
        </PageHeading>
        <NavRibbon links={[
            new NavRibbonLink(
                Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(inventoryDetails.id),
                'la-users',
                'Control Groups',
                [Permissions.InventoryPhysicalControlGroupsView]
            ),
            new NavRibbonLink(
                Routes.INVENTORY_PHYSICAL_DETAILS_SHIPMENTS.formattify(inventoryDetails.id),
                'la-truck',
                'Shipments',
                [Permissions.InventoryPhysicalShipmentsView]
            ),
            new NavRibbonLink(
                Routes.INVENTORY_PHYSICAL_DETAILS_ORDERS.formattify(inventoryDetails.id),
                'la-cube',
                'Orders',
                [Permissions.InventoryPhysicalOrdersView]
            ),
            new NavRibbonLink(
                Routes.INVENTORY_PHYSICAL_DETAILS_ADJUSTMENTS.formattify(inventoryDetails.id),
                'la-edit',
                'Adjustments',
                [Permissions.InventoryPhysicalAdjustmentsView]
            ),
            new NavRibbonLink(
                Routes.INVENTORY_PHYSICAL_DETAILS_REPORT.formattify(inventoryDetails.id),
                'la-file-text',
                'Reports',
                [Permissions.InventoryPhysicalReportsView]
            )
        ]} />

        <Switch>

            { /* Inventory Routes */}
            <Route
                key="inventory-physical-controlgroups"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS_ROUTE}
                render={() => <InventoryPhysicalControlGroupsScene {...props} /> }
            />

            <Route
                key="inventory-physical-horizontal-entry"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_HORIZONTAL_ENTRIES_ROUTE}
                render={() => <InventoryPhysicalHorizontalEntryEditScene {...props} ref={editEntryRef} />}
            />

            <Route
                key="inventory-physical-vertical-entry"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_VERTICAL_ENTRIES_ROUTE}
                render={() => <InventoryPhysicalVerticalEntryEditScene {...props} ref={editEntryRef} />}
            />

            <Route
                key="inventory-physical-resolve-conflicts"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_CONFLICTS_ROUTE}
                render={() => <InventoryPhysicalResolveConflictsScene {...props} ref={editEntryRef} />}
            />

            <Route
                key="inventory-physical-shipments"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_SHIPMENTS_ROUTE}
                render={() => <InventoryPhysicalShipmentsScene {...props} ref={shipmentsRef} />}
            />

            <Route
                key="inventory-physical-orders"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_ORDERS_ROUTE}
                render={() => <InventoryPhysicalOrdersScene {...props} ref={ordersRef} />}
            />

            <Route
                key="inventory-physical-adjustments"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_ADJUSTMENTS_ROUTE}
                render={() => <InventoryPhysicalAdjustmentsScene {...props} ref={adjustmentsRef} />}
            />

            <Route
                key="inventory-physical-report"
                exact={true}
                path={Routes.INVENTORY_PHYSICAL_DETAILS_REPORT_ROUTE}
                render={() => <InventoryPhysicalReportScene {...props} ref={reportRef} />}
            />

            <Redirect
                key="inventory-pyhsical-redirect"
                exact={true}
                from={Routes.INVENTORY_PHYSICAL_DETAILS_WITH_ROUTE}
                to={Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(getId())} />

        </Switch>
    </PageContent>;
};